import {Directive, effect, inject, input, OnDestroy, TemplateRef, ViewContainerRef} from '@angular/core';
import {PermissionType, UserRoleType} from '@core/api/data-access';
import {PwaManagerService} from '@pwa';
import {AuthStore} from '../+store/auth.store';
import {ActiveUserStore} from '@active-user-management';

/**
 * example usage:
 * <some-component *permissionGuard="{
 * allow: [PermissionType.cycle_create], //PermissionType or UserRoleType Array
 * denied: [PermissionType.sysadmin], //PermissionType or UserRoleType Array
 * }"
 * >
 *....
 * </some-component>
 */

type permissionGuardDirectiveSchema = {
	allow?: (PermissionType | UserRoleType)[];
	denied?: (PermissionType | UserRoleType)[];
	pwaOnly?: boolean;
};

@Directive({
	selector: '[permissionGuard]',
	exportAs: 'permissionGuard',
	standalone: true, // Ensure directive is standalone
})
export class ActiveUserPermissionsDirective implements OnDestroy {
	private readonly templateRef: TemplateRef<any> = inject(TemplateRef);
	private readonly viewContainerRef: ViewContainerRef = inject(ViewContainerRef);
	private readonly authStore = inject(AuthStore);
	private readonly pwaManagerService = inject(PwaManagerService);
	readonly activeUserStore: ActiveUserStore = inject(ActiveUserStore);

	public readonly permissionGuard = input<permissionGuardDirectiveSchema>(undefined);

	private hasView = false; // Flag to track if the view is currently created

	constructor() {
		effect(() => {
			// Read all dependent signals at the beginning of the effect
			const permissionGuardData = this.permissionGuard();
			const activeUserPermissions = this.authStore.activeUserPermissions$();
			const activeUser = this.activeUserStore.user$();
			const isPwa = this.pwaManagerService.isPwaMode$();

			let shouldShow = true; // Default to show if no rules apply

			if (permissionGuardData) {
				const allow = permissionGuardData.allow || [];
				const denied = permissionGuardData.denied || [];

				// Only evaluate permissions if allow or denied lists are provided
				if (allow.length > 0 || denied.length > 0) {
					const activeUserRoles = activeUser?.roles || [];
					const activeUserPermissionsAndRoles = [...activeUserRoles, ...activeUserPermissions];

					// Check allow list: User must have ALL permissions/roles specified
					if (allow.length > 0) {
						shouldShow = allow.every(permission => activeUserPermissionsAndRoles.includes(permission));
					}

					// Check denied list: User must NOT have ANY permission/role specified
					// This check only runs if the 'allow' check passed (or was skipped)
					if (shouldShow && denied.length > 0) {
						shouldShow = !denied.some(permission => activeUserPermissionsAndRoles.includes(permission));
					}
				}

				// Check pwaOnly flag: If true, visibility requires PWA mode
				// This check is combined with the permission checks
				if (permissionGuardData.pwaOnly) {
					shouldShow = shouldShow && isPwa;
				}
			}

			// Update the view only if the state needs to change
			if (shouldShow && !this.hasView) {
				// Create the view if it should be shown and isn't already
				this.viewContainerRef.createEmbeddedView(this.templateRef);
				this.hasView = true;
			} else if (!shouldShow && this.hasView) {
				// Clear the view if it shouldn't be shown and currently is
				this.viewContainerRef.clear();
				this.hasView = false;
			}
			// If shouldShow === this.hasView, do nothing (view state is already correct)
		});
	}

	ngOnDestroy(): void {
		// Ensure the view container is cleared when the directive is destroyed
		this.viewContainerRef.clear();
	}
}
